import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Card,
  Container,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Box,
  Tooltip,
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { usePopup } from '../../../context/PopupContext';

import { LoadingUI } from '../../../utils/LayoutStyles';
import { DefaultContainedButton, CustomIconButton, DefaultOutlinedButton } from '../../../utils/Buttons';
import { CommonLabel } from '../../../utils/Common';
import { ErrorUI } from '../../../utils/ErrorHandler';
import API from '../../../api';

export default function BankPaymentBatch() {
  const navigate = useNavigate();

  const [batches, setBatches] = useState([]);
  const [isBatchLoading, setIsBatchLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const getBatchList = () => {
    API.get('/bankpaybatch')
      .then((response) => {
        setBatches(response.data);
        setIsBatchLoading(false);
      })
      .catch((error) => {
        setIsBatchLoading(false);
      });
  };

  useEffect(() => {
    if (batches.length === 0 && isBatchLoading) {
      getBatchList();
    }
  }, []);

  const [openUploadCSV, setOpenUploadCSV] = useState(false);

  const handleOpenUploadCSV = () => {
    setOpenUploadCSV(true);
  };

  const handleCloseUploadCSV = () => {
    setOpenUploadCSV(false);
    setFile(null);
    setData([]);
  };

  const [file, setFile] = useState(null);
  const [data, setData] = useState([]);
  const fileReader = new FileReader();

  // File Change Handler
  const handleOnChange = (e) => {
    setFile(e.target.files[0]);
  };

  // CSV Reader
  useEffect(() => {
    if (file) {
      fileReader.onload = function (event) {
        const csvOutput = event.target.result;
        const csvArray = csvToArray(csvOutput);
        setData(csvArray);
      };

      fileReader.readAsText(file);
    }
  }, [file]);

  // CSV To Array Converter
  const csvToArray = (str, delimiter = ',') => {
    const headers = str.slice(0, str.indexOf('\n')).split(delimiter);
    const rows = str.slice(str.indexOf('\n') + 1).split('\n');

    const array = rows.map((row) => {
      const values = row.split(delimiter);
      const obj = headers.reduce((acc, header, i) => {
        acc[header] = values[i];
        return acc;
      }, {});
      return obj;
    });

    return { items: array };
  };

  // Submit Save CSV Array
  const handleSubmitData = (e) => {
    e.preventDefault();

    const bankData = [];

    for (let i = 0; i < data.items.length; i += 1) {
      const item = {
        payment_id: data.items[i].payment_id,
        bank_name: data.items[i].bank_name,
        bank_account_id: data.items[i].bank_account_id,
        currency: data.items[i].currency,
        bank_owner_name: data.items[i].bank_owner_name,
        transfer_date: data.items[i].transfer_date,
        transfer_registration_date: data.items[i].transfer_registration_date,
        amount: Number(data.items[i].amount),
        bank_transfer_reason: data.items[i].bank_transfer_reason,
        processed: data.items[i].processed === '0' ? 0 : 1,
      };

      bankData.push(item);
    }

    const result = {
      items: bankData.slice(0, -1),
    };

    API.post('/bankpaybatch', result)
      .then((response) => {
        showPopup(true, 'Dati salvati con successo');

        setTimeout(() => {
          handleCloseUploadCSV();
          setIsBatchLoading(true);
          getBatchList();
        }, 2000);
      })
      .catch((error) => {
        showPopup(false, error.response.data.message);
      });
  };

  const [openDeleteBatch, setOpenDeleteBatch] = useState(false);
  const [batchToDelete, setBatchToDelete] = useState({});

  const handleOpenDeleteBatch = (batch) => {
    setBatchToDelete(batch);
    setOpenDeleteBatch(true);
  };

  const handleCloseDeleteBatch = () => {
    setBatchToDelete({});
    setOpenDeleteBatch(false);
  };

  const handleDeleteBatch = () => {
    API.delete(`/bankpaybatch/${batchToDelete.id}`)
      .then((response) => {
        handleCloseDeleteBatch();
        isBatchLoading();
        getBatchList();
      })
      .catch((error) => {
        showPopup(false, error.response.data.message);
      });
  };

  const { showPopup, setPopUpText } = usePopup();

  return (
    <>
      <Helmet>
        <title>Bank Payment Batch</title>
      </Helmet>

      <Container maxWidth="xl">
        {errorMessage?.length === 0 ? (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Dialog open={openUploadCSV} onClose={() => {}} fullWidth>
                <DialogTitle>Seleziona il file CSV</DialogTitle>
                <DialogContent>
                  {file === null ? (
                    <form>
                      <Button
                        sx={{
                          height: '200px',
                          width: '100%',
                          boxShadow: 'none',
                          backgroundColor: '#f4f6f8',
                          color: 'black',
                          '&:hover': {
                            backgroundColor: '#f4f6f8',
                          },
                        }}
                        variant="contained"
                        component="label"
                      >
                        Upload CSV File
                        <input type={'file'} id={'csvFileInput'} accept={'.csv'} onChange={handleOnChange} hidden />
                      </Button>
                    </form>
                  ) : (
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Box
                        component="img"
                        src={'/assets/icons/ic_csv.svg'}
                        sx={{ width: 48, height: 48, flexShrink: 0 }}
                      />

                      <Stack direction="column" sx={{ minWidth: 240, flexGrow: 1 }}>
                        <Typography color="inherit" variant="subtitle2" noWrap>
                          {file.name}
                        </Typography>

                        <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
                          Dimensione file: {file.size}
                        </Typography>

                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Ultima modifica: {dayjs(file.lastModifiedDate).format('DD/MM/YYYY')}
                        </Typography>
                      </Stack>
                    </Stack>
                  )}
                </DialogContent>
                <DialogActions>
                  <DefaultOutlinedButton onClick={handleCloseUploadCSV}>Chiudi</DefaultOutlinedButton>
                  {file !== null && (
                    <DefaultContainedButton onClick={handleSubmitData}>Salva Dati</DefaultContainedButton>
                  )}

                </DialogActions>
              </Dialog>

              <Stack direction="row" alignItems="center" justifyContent="end">
                <DefaultContainedButton startIcon={<FileUploadIcon />} onClick={handleOpenUploadCSV}>
                  Upload File Banca
                </DefaultContainedButton>
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <Dialog open={openDeleteBatch} onClose={() => {}} fullWidth>
                <DialogTitle>Eliminazione Batch</DialogTitle>
                <DialogContent>Sei sicuro di voler eliminare il batch n° {batchToDelete.batch_no}?</DialogContent>
                <DialogActions>
                  <DefaultOutlinedButton onClick={handleCloseDeleteBatch}>Chiudi</DefaultOutlinedButton>
                  <DefaultContainedButton onClick={handleDeleteBatch}>Elimina</DefaultContainedButton>
                </DialogActions>
              </Dialog>

              <Card sx={{ padding: '24px' }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Data Creazione</TableCell>
                        <TableCell>Batch n°</TableCell>
                        <TableCell>Processato</TableCell>
                        <TableCell />
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>

                    {isBatchLoading ? (
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={4} sx={{ textAlign: 'center' }}>
                            {LoadingUI()}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    ) : batches.length === 0 ? (
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={4} sx={{ textAlign: 'center' }}>
                            Nessun dato disponibile
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    ) : (
                      <TableBody>
                        {batches.map((batch) => (
                          <TableRow key={batch.batch_no} hover>
                            <TableCell>{dayjs(batch.created_at).format('DD/MM/YYYY')}</TableCell>
                            <TableCell>{batch.batch_no}</TableCell>
                            <TableCell>
                              <CommonLabel
                                textColor={batch.completed ? '#118d57' : '#b71d18'}
                                bgColor={batch.completed ? '#d3efdf' : '#f7ddd8'}
                                text={batch.completed ? 'Completato' : 'In Corso'}
                              />
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Elimina Batch" arrow placement="right">
                                <Box sx={{ width: 'fit-content' }}>
                                  <CustomIconButton onClick={() => handleOpenDeleteBatch(batch)}>
                                    <DeleteIcon />
                                  </CustomIconButton>
                                </Box>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">
                              <CustomIconButton
                                onClick={() =>
                                  navigate('/admin/bankpayment/details', {
                                    state: { batchID: batch.id, batchNo: batch.batch_no },
                                  })
                                }
                              >
                                <KeyboardArrowRightIcon />
                              </CustomIconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    )}
                  </Table>
                </TableContainer>
              </Card>
            </Grid>
          </Grid>
        ) : (
          ErrorUI(errorMessage)
        )}
      </Container>
    </>
  );
}
